import { createElement } from 'react';
import { TInvoice } from 'redux/reducers/invoices/types';
import { INVOICES_STATUS } from 'redux/reducers/invoices/constants';
import {
	ETableStructureAlign,
	ETableStructureType,
	TTableStructure,
} from 'services/constants/tableStructureTypes';
import { DATES, DATES_ID } from 'services/constants/dates';
import { durationDates } from 'utils/durationDates';
import { AmountSpeedDial } from './InvoicesRow/AmountSpeedDial';
import { EIconButtons } from 'services/constants/iconsTypes';
import { TTableStructureButton } from 'services/constants/types';

const DATE_FILTER: [string, string][] = [
	[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
	[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
	[DATES_ID.THIS_QUARTER, DATES.THIS_QUARTER],
	[DATES_ID.LAST_QUARTER, DATES.LAST_QUARTER],
	[DATES_ID.THIS_YEAR, DATES.THIS_YEAR],
	[DATES_ID.LAST_YEAR, DATES.LAST_YEAR],
	[DATES_ID.CUSTOM_RANGE, DATES.CUSTOM_RANGE],
];

const INVOICE_STATUS_FILTER: [string, string][] = [
	[INVOICES_STATUS.PAID, 'paid'],
	[INVOICES_STATUS.PARTIALLY_PAID, 'partially paid'],
	[INVOICES_STATUS.OVERDUE, 'overdue'],
	[INVOICES_STATUS.CARRIED_OVER, 'carried over'],
	[INVOICES_STATUS.PENDING, 'pending'],
];

const INVOICE_STATUS_COLORS: [string, string][] = [
	[INVOICES_STATUS.PAID, 'success.light'],
	[INVOICES_STATUS.PARTIALLY_PAID, 'warning.light'],
	[INVOICES_STATUS.OVERDUE, 'error.light'],
	[INVOICES_STATUS.CARRIED_OVER, '#d5d5d5'],
	[INVOICES_STATUS.PENDING, 'primary.light'],
];

export const INVOICES_ACTIONS = {
	DOWNLOAD_PDF: 'Invoice',
	DOWNLOAD_CSV: 'Breakdown',
	// payment page of the payment gateway (link from dashboard-new-api)
	PAY_ONLINE: 'Pay online',
	// "Make a payment" with a saved payment method (payment gateway, api/invoices/payable)
	PAY_WITH_SAVED_METHOD: 'Pay with saved method',
	// receipt of the online payment (payment gateway, api/invoices/receipts)
	DOWNLOAD_RECEIPT: 'Receipt',
} as const;

/** `true` — the invoice can be paid now with a saved payment method (it is in `payable` with `payable=true`). */
export type TCanPayWithSavedMethod = (invoice: TInvoice) => boolean;

/** `true` — the invoice was paid online through the payment gateway and has a payment receipt. */
export type THasPaymentReceipt = (invoice: TInvoice) => boolean;

const formatInvoicingPeriod = (duration: string): string => {
	const intervalStringArray = durationDates(duration);
	if (!intervalStringArray?.length) return '';

	if (intervalStringArray.length === 1) {
		return `to ${intervalStringArray[0].slice(0, 11)}`;
	}

	return `${intervalStringArray[0].slice(0, 11)} - ${intervalStringArray[1].slice(0, 11)}`;
};

/**
 * `/api2/invoice/list` supports sorting and filtering on `id`, `invoiceNumber`,
 * `referenceNumber`, `invoiceDate`, `dueIn`, `paymentStatus` and `invoiceStatus` only.
 */
export const buildInvoicesTableStructure = (
	canPayWithSavedMethod: TCanPayWithSavedMethod,
	hasPaymentReceipt: THasPaymentReceipt = () => false,
): TTableStructure<keyof TInvoice, TInvoice>[] => [
	{
		name: 'Invoice #',
		selector: 'invoiceNumber',
		show: true,
		sortable: true,
		separateFilter: true,
		colSpan: 1,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Reference #',
		selector: 'referenceNumber',
		show: true,
		sortable: true,
		separateFilter: true,
		colSpan: 1,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Issue Date',
		selector: 'invoiceDate',
		show: true,
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
		colSpan: 1,
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.CENTER,
	},
	{
		name: 'Due Date',
		selector: 'dueIn',
		show: true,
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
		colSpan: 1,
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.CENTER,
	},
	{
		name: 'Invoicing Period',
		selector: 'duration',
		show: true,
		sortable: false,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.STATUS_NO_BG,

		align: ETableStructureAlign.CENTER,
		value: (item: TInvoice) => formatInvoicingPeriod(item.duration),
	},
	{
		name: 'Reimbursed VAT from',
		selector: 'reimbursedCountries',
		show: true,
		sortable: false,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.COUNTRY_CODE_ARRAY,
		align: ETableStructureAlign.LEFT,
		value: (item: TInvoice) =>
			(item.reimbursedCountries
				? item.reimbursedCountries.split(', ').filter(Boolean)
				: []) as unknown as string,
	},
	{
		name: '',
		selector: 'monthlyFee',
		show: true,
		noName: true,
		sortable: false,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		currency: (item: TInvoice) => item.invoiceDue?.[1]?.currency ?? 'EUR',
		value: (item: TInvoice) => {
			if (item.invoiceDue?.length <= 1 || !item.invoiceDue?.[1]?.dueAmount) return null;
			return String(item.invoiceDue[1].dueAmount);
		},
	},
	{
		name: 'Paid Amount',
		selector: 'paidAmount',
		show: true,
		sortable: false,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		currency: (item: TInvoice) => item.invoiceDue?.[0]?.currency ?? 'EUR',
		value: (item: TInvoice) => (item.paidAmount ? String(item.paidAmount) : '—'),
	},
	{
		name: 'Amount',
		selector: 'easFeeTotal',
		show: true,
		sortable: false,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		value: (item: TInvoice) => {
			if (!item.invoiceDue?.length || !item.invoiceDue[0]?.dueAmount) return '—';
			return String(item.invoiceDue[0].dueAmount);
		},
		breakdown: (item: TInvoice) => {
			if (!item.invoiceDue?.[0]?.dueAmount) return null;

			return createElement(AmountSpeedDial, {
				easFee: item.easFeeTotal,
				monthlyFee: item.monthlyFee,
				invoicingFee: item.invoicingFee,
				vatOnEasFeeAndInvoicingFee: +(item.vatOnEasFeeAndInvoicingFee ?? 0),
				vatRate: item.vatRate,
				vatDueToEas: item.vatDueToEas,
				delayedPaymentFee: item.delayedPaymentFee,
				invoiceCarryOverAmount: item.invoiceCarryOverAmount,
				total: item.invoiceDue[0].dueAmount,
				delayedInvoiceNumber: item.delayedInvoiceNumber,
				customsVat: item.customsVat,
				easCustomsFee: item.easCustomsFee,
				customsDuties: item.customsDuties,
				reimbursedCountries: item.reimbursedCountries,
				reimbursedAmount: item.reimbursedAmount,
			});
		},
	},
	{
		name: 'Invoice status',
		selector: 'invoiceStatus',
		show: true,
		sortable: true,
		filterable: true,
		filter: INVOICE_STATUS_FILTER,
		colors: INVOICE_STATUS_COLORS,
		colSpan: 1,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Download',
		selector: 'id',
		show: false,
		sortable: false,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.BUTTONS,
		align: ETableStructureAlign.CENTER,
		buttonFilter: () => [
			{
				name: INVOICES_ACTIONS.DOWNLOAD_PDF,
				icon: EIconButtons.CLOUD_DOWNLOAD,
				iconWithName: true,
			},
			{
				name: INVOICES_ACTIONS.DOWNLOAD_CSV,
				icon: EIconButtons.CLOUD_DOWNLOAD,
				iconWithName: true,
			},
		],
	},
	{
		name: 'Payment',
		noName: true,
		selector: 'paymentStatus',
		show: true,
		sortable: false,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.BUTTONS,
		align: ETableStructureAlign.LEFT,
		buttonFilter: (item: TInvoice) => {
			const buttons: TTableStructureButton[] = [
				{
					name: INVOICES_ACTIONS.DOWNLOAD_PDF,
					icon: EIconButtons.CLOUD_DOWNLOAD,
					iconWithName: true,
					tooltip: 'Download invoice',
				},
				{
					name: INVOICES_ACTIONS.DOWNLOAD_CSV,
					icon: EIconButtons.CLOUD_DOWNLOAD,
					iconWithName: true,
					tooltip: 'Download breakdown',
				},
			];
			if (hasPaymentReceipt(item)) {
				buttons.push({
					name: INVOICES_ACTIONS.DOWNLOAD_RECEIPT,
					icon: EIconButtons.RECEIPT,
					iconWithName: true,
					tooltip: 'Download payment receipt',
				});
			}
			if (isPaymentDisabled(item)) return buttons;
			const payButtons: TTableStructureButton[] = [
				{
					name: INVOICES_ACTIONS.PAY_ONLINE,
					icon: EIconButtons.CARD,
					iconWithName: true,
					tooltip: 'Pay online',
				},
			];
			if (canPayWithSavedMethod(item)) {
				payButtons.push({
					name: INVOICES_ACTIONS.PAY_WITH_SAVED_METHOD,
					icon: EIconButtons.CHECK_CIRCLE,
					iconWithName: true,
					tooltip: 'Pay with saved method',
				});
			}
			return [...buttons, ...payButtons];
		},
	},
];

export const invoicesTableStructure = buildInvoicesTableStructure(() => false);

/**
 * Only paid invoices have no payment buttons. Whether an invoice may be paid (the rule for an outstanding
 * balance transferred to the subsequent invoice) is decided by the payment gateway: the payment page shows
 * the reason, and "Pay with saved method" is offered only for invoices the gateway reports as payable.
 */
export const isPaymentDisabled = (invoice: TInvoice): boolean =>
	invoice.invoiceStatus === INVOICES_STATUS.PAID;
